if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      variantData = undefined;
      currentSelectedOptionValues = undefined;

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      /* ---------------------------------------------------------------- */
      /*  Variant resolution helpers                                      */
      /* ---------------------------------------------------------------- */

      getSelectedOptionValues() {
        const selects = this.querySelector('variant-selects');
        if (!selects) return [];

        return Array.from(selects.querySelectorAll('select, fieldset'))
          .map((element) => {
            if (element.tagName === 'SELECT') return element.value;
            const checked = element.querySelector('input:checked');
            return checked ? checked.value : null;
          })
          .filter(Boolean);
      }

      // `selectedOptionValues` coming from the `variant-selects` component's
      // change event is an array of numeric option-VALUE-ID strings (see
      // component-variant-selects.js: `dataset.optionValueId`), NOT the option
      // value text ("Granite", "S", etc). Everything else in this file - the
      // `data-variants` JSON, `getSelectedOptionValues()` above, and every
      // variant-matching helper below - works in terms of the option value TEXT,
      // because that's what `variant.options` contains. Without normalizing IDs
      // back to text here, every option click would compare an id like
      // "4738050490540" against a text value like "Granite" - which never
      // matches - and every variant would resolve to null, permanently disabling
      // the buy button even for perfectly valid, in-stock selections.
      normalizeOptionValues(optionValues) {
        if (!optionValues || !optionValues.length) return optionValues || [];

        const selects = this.querySelector('variant-selects');
        if (!selects) return optionValues;

        // Map every rendered option input/option element's value-id -> its text value.
        const idToValue = new Map();
        selects.querySelectorAll('[data-option-value-id]').forEach((element) => {
          const id = element.dataset.optionValueId;
          const value = element.value;
          if (id != null && value) idToValue.set(String(id), value);
        });

        return optionValues.map((entry) => {
          if (entry == null) return entry;
          // Already a text value (not a known id) - leave it alone.
          if (!idToValue.has(String(entry))) return entry;
          return idToValue.get(String(entry));
        });
      }

      getVariantData() {
        this.variantData =
          this.variantData ||
          JSON.parse(this.querySelector('variant-selects [data-variants]')?.textContent || '[]');
        return this.variantData;
      }

      findVariantByOptionValues(optionValues) {
        if (!optionValues || !optionValues.length) return null;
        return this.getVariantData().find((variant) => {
          return variant.options.every((option, index) => option === optionValues[index]);
        });
      }

      // Find the closest variant when there's no exact match for the full option
      // selection (e.g. a Design+Color+Size combination that doesn't exist as a
      // real variant, like an out-of-stock size). The product's photo only ever
      // depends on options like Design/Color, never on Size, so we want to keep
      // showing the right picture for the selected Design+Color even when the
      // exact Size doesn't exist for that combo.
      //
      // Approach: score every variant by how many *leading* option positions
      // match the current selection (Design, then Color, then ...), and return
      // the variant with the best score. Because most Shopify products order
      // options as [Design, Color, Size] (Size last), this naturally prefers a
      // variant that shares Design+Color over one that happens to share Size but
      // not Design/Color - matching how the product actually looks. Ties are
      // broken by preferring an available variant, then by preferring one whose
      // remaining (non-matching) option is the LAST position, since that's where
      // Size conventionally lives.
      findClosestVariantByOptionValues(optionValues) {
        if (!optionValues || !optionValues.length) return null;
        const variants = this.getVariantData();
        if (!variants.length) return null;

        let best = null;
        let bestScore = -1;

        for (const variant of variants) {
          if (!Array.isArray(variant.options)) continue;

          let leadingMatches = 0;
          for (let i = 0; i < optionValues.length; i++) {
            if (variant.options[i] === optionValues[i]) {
              leadingMatches++;
            } else {
              break;
            }
          }

          if (leadingMatches === 0) continue;

          // Prefer more leading matches, then prefer variants that are available.
          const score = leadingMatches * 2 + (variant.available ? 1 : 0);
          if (score > bestScore) {
            bestScore = score;
            best = variant;
          }
        }

        return best;
      }

      // Only treat a variant object as valid for the CURRENT selection if its own
      // options actually match what's selected. Shopify's Liquid
      // `product.selected_or_first_available_variant` (used to render the
      // `data-selected-variant` script tag) never returns null - if the requested
      // option combination doesn't correspond to any real variant, it silently
      // falls back to "the first available variant of the product", which can be a
      // totally unrelated variant (different color/design/etc). Without this
      // guard, that unrelated variant gets treated as if it were genuinely
      // selected: its image gets shown, its `available: true` re-enables the buy
      // button, and its id gets written into the hidden variant input - even
      // though the options on screen don't match it at all.
      variantMatchesOptionValues(variant, optionValues) {
        if (!variant || !optionValues || !optionValues.length) return false;
        return variant.options.every((option, index) => option === optionValues[index]);
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode?.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      getSelectedVariantFromCurrentInputs() {
        const variantSelects = this.querySelector('variant-selects');
        const selectedVariant = variantSelects?.querySelector('[data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      getMediaIdForUnavailableState(html, variant) {
        // `currentSelectedOptionValues` may hold numeric option-value IDs (when
        // sourced from the variant-selects component's change event) rather
        // than the option value text that `variant.options` in the JSON data
        // uses - normalize so every comparison below is text-vs-text.
        const optionValues = this.normalizeOptionValues(
          this.currentSelectedOptionValues || this.getSelectedOptionValues()
        );

        // 1. The resolved variant has its own dedicated image - always prefer it,
        //    regardless of whether the variant is in stock. `variant` is already
        //    guarded by the caller to only be non-null when it truly matches the
        //    selected options (see variantMatchesOptionValues).
        if (variant?.featured_media?.id != null) return String(variant.featured_media.id);

        // 2. No EXACT variant exists for the full selection (e.g. a Size that
        //    doesn't exist for the current Design+Color, so it's an
        //    out-of-stock/nonexistent combination). The product photo is driven
        //    by options like Design/Color, never by Size, so every other Size of
        //    the same Design+Color already has the correct picture - find the
        //    closest real variant that shares as many leading options
        //    (Design, Color, ...) as possible and borrow its image. This keeps
        //    the image correct for the selected look even though that exact
        //    size isn't purchasable.
        const closestVariant = this.findClosestVariantByOptionValues(optionValues);
        if (closestVariant?.featured_media?.id != null) {
          return String(closestVariant.featured_media.id);
        }

        // 3. Fall back to whatever variant the freshly-fetched section resolved as
        //    "selected", but ONLY if its own options actually match what's
        //    currently selected. Shopify's `product.selected_or_first_available_variant`
        //    (which renders the `data-selected-variant` script tag) never returns
        //    null - for an option combination with no real variant, it silently
        //    substitutes "the first available variant of the product", which can
        //    be a completely unrelated color/design. Trusting that blindly is
        //    what previously caused the image to jump to an unrelated variant's
        //    photo instead of reflecting the actual (unavailable) selection.
        const selectedVariantFromHtml = this.getSelectedVariant(html);
        if (
          this.variantMatchesOptionValues(selectedVariantFromHtml, optionValues) &&
          selectedVariantFromHtml?.featured_media?.id != null
        ) {
          return String(selectedVariantFromHtml.featured_media.id);
        }

        // 4. No variant-specific image exists anywhere close to this selection.
        //    Previously this fell through to "whatever image is already on
        //    screen", which left the old variant's photo showing (or, worse, an
        //    unrelated variant's photo) and made the page look broken. Instead,
        //    always resolve to a real, current image: use the product's default
        //    featured media so the gallery visibly and correctly updates on every
        //    click. Availability itself is still communicated separately via the
        //    disabled buy button / sold-out state, not by the image shown.
        const productDefaultMediaId = this.querySelector('variant-selects [data-product-featured-media-id]')
          ?.dataset.productFeaturedMediaId;
        if (productDefaultMediaId) return String(productDefaultMediaId);

        const firstMedia = this.querySelector('media-gallery [data-media-id]');
        if (!firstMedia) return null;

        const mediaId = firstMedia.dataset.mediaId || '';
        const parts = mediaId.split('-');
        return parts.length ? String(parts[parts.length - 1]) : null;
      }

      /* ---------------------------------------------------------------- */
      /*  Quantity handlers                                                */
      /* ---------------------------------------------------------------- */

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      /* ---------------------------------------------------------------- */
      /*  Option value change / fetch                                     */
      /* ---------------------------------------------------------------- */

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.currentSelectedOptionValues = selectedOptionValues || this.getSelectedOptionValues();
        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, this.currentSelectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      /* ---------------------------------------------------------------- */
      /*  Main update handler                                             */
      /* ---------------------------------------------------------------- */

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          // Same ID-vs-text normalization as getMediaIdForUnavailableState -
          // `findVariantByOptionValues` / `variantMatchesOptionValues` compare
          // against `variant.options`, which is always option value text.
          const optionValues = this.normalizeOptionValues(
            this.currentSelectedOptionValues || this.getSelectedOptionValues()
          );

          let variant =
            this.findVariantByOptionValues(optionValues) ||
            [this.getSelectedVariant(html), this.getSelectedVariantFromCurrentInputs()].find((candidate) =>
              this.variantMatchesOptionValues(candidate, optionValues)
            ) ||
            null;

          const fallbackMediaId = this.getMediaIdForUnavailableState(html, variant);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          updateSourceFromDestination.call(this, html, 'price');
          updateSourceFromDestination.call(this, html, 'Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination.call(this, html, 'Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination.call(this, html, 'Volume');
          updateSourceFromDestination.call(this, html, 'Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.updateMedia(html, fallbackMediaId);
          this.updateOosOverlay(!(variant && variant.available));

          if (!variant) {
            this.setUnavailable();
            publish(PUB_SUB_EVENTS.variantChange, {
              data: {
                sectionId: this.sectionId,
                html,
                variant: null,
              },
            });
            return;
          }

          this.productForm?.toggleSubmitButton(
            !variant.available,
            variant.available ? window.variantStrings.addToCart : window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };

        function updateSourceFromDestination(htmlDoc, id, shouldHide = () => false) {
          const source = htmlDoc.getElementById(`${id}-${this.sectionId}`);
          const destination = this.querySelector(`#${id}-${this.dataset.section}`);
          if (source && destination) {
            destination.innerHTML = source.innerHTML;
            destination.classList.toggle('hidden', shouldHide(source));
          }
        }
      }

      /* ---------------------------------------------------------------- */
      /*  Variant / URL / availability updates                            */
      /* ---------------------------------------------------------------- */

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);
        this.updateOosOverlay(true);

        const priceEl = this.querySelector(`#price-${this.dataset.section}`);
        if (priceEl) {
          priceEl.classList.remove('hidden');
        }

        const inventoryEl = this.querySelector(`#Inventory-${this.dataset.section}`);
        if (inventoryEl) {
          inventoryEl.classList.remove('hidden');
          if (!inventoryEl.textContent.trim()) {
            inventoryEl.innerHTML = '<span class="product__oos-message">Unavailable</span>';
          }
        }

        const selectors = ['Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateOosOverlay(isUnavailable) {
        const mediaWrapper = this.querySelector('.product__media-wrapper');
        if (!mediaWrapper) return;
        mediaWrapper.classList.toggle('product__media-wrapper--oos', isUnavailable);
      }

      /* ---------------------------------------------------------------- */
      /*  Media gallery sync                                              */
      /* ---------------------------------------------------------------- */

      // Full map of EVERY media id this product can ever need, built once (and
      // cached) directly from the product's own variant JSON
      // (`variant-selects [data-variants]`, already used by getVariantData()) -
      // NOT from whatever happens to come back in a given AJAX section fetch.
      //
      // This matters because the theme's gallery is deliberately capped to a
      // small handful of items per request (server-side, in Liquid) - a fresh
      // fetch for an unavailable/nonexistent option combo (e.g. a Size that
      // doesn't exist for the selected Design+Color) NEVER includes the real
      // image for the color we actually want; Shopify resolves the invalid
      // combo to its own arbitrary substitute variant server-side and only
      // ever sends back that substitute's image plus a handful of unrelated
      // "filler" images. No sequence of AJAX fetches will ever deliver every
      // color's real photo, so remembering only what we happen to see fetched
      // (the previous approach) could only ever work for whichever variant
      // was on the page at initial load - every other color remained
      // permanently unrecoverable. Building the map from the variants JSON
      // instead guarantees every real Design+Color image is available
      // up front, regardless of what any individual fetch chooses to include.
      getMediaNodeMap() {
        if (this.knownMediaNodes) return this.knownMediaNodes;
        this.knownMediaNodes = new Map();

        const sectionId = this.dataset.section;
        const gallery = this.querySelector('media-gallery ul');
        // Use any currently-rendered <li> as a structural template so the
        // node we build matches this theme's real markup (classes, media
        // wrapper structure, etc) rather than a hand-rolled approximation.
        const templateNode = gallery?.querySelector('li[data-media-id]');

        const seenMediaIds = new Set();
        this.getVariantData().forEach((variant) => {
          const media = variant.featured_media;
          if (!media || media.id == null || seenMediaIds.has(media.id)) return;
          seenMediaIds.add(media.id);

          const mediaIdAttr = `${sectionId}-${media.id}`;
          // Prefer an already-rendered real <li> for this exact media id (most
          // accurate markup - video/model support, srcset, etc), falling back
          // to a constructed one only when this media id was never rendered
          // anywhere on the page.
          const existingNode = gallery?.querySelector(`li[data-media-id="${mediaIdAttr}"]`);
          if (existingNode) {
            this.knownMediaNodes.set(mediaIdAttr, existingNode.cloneNode(true));
            return;
          }

          const src = media.preview_image?.src;
          if (!src || !templateNode) return;

          const node = templateNode.cloneNode(true);
          node.id = `Slide-${sectionId}-${media.id}`;
          node.dataset.mediaId = mediaIdAttr;
          node.classList.remove('is-active');
          const img = node.querySelector('img');
          if (img) {
            const normalizedSrc = src.startsWith('//') ? `https:${src}` : src;
            img.src = normalizedSrc;
            img.srcset = '';
            img.alt = media.alt || img.alt || '';
          }
          this.knownMediaNodes.set(mediaIdAttr, node);
        });

        return this.knownMediaNodes;
      }

      getRememberedMediaNode(mediaIdAttr) {
        return this.getMediaNodeMap().get(mediaIdAttr)?.cloneNode(true) || null;
      }

      updateMedia(html, variantFeaturedMediaId) {
        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector('media-gallery ul');

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        // The media id we're about to activate, in the same "{sectionId}-{mediaId}"
        // form used by setActiveMedia/data-media-id.
        const pinnedMediaId =
          variantFeaturedMediaId != null && variantFeaturedMediaId !== ''
            ? `${this.dataset.section}-${String(variantFeaturedMediaId)}`
            : null;

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // Never prune a media item that we might need to re-activate later -
          // specifically the one we're pinning right now. Everything else
          // still gets pruned normally when it's a genuinely valid section
          // swap (e.g. switching products), matching the theme's original
          // behaviour there.
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (
              !destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId) &&
              mediaGallerySourceItems[i].dataset.mediaId !== pinnedMediaId
            ) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // If the media we need to activate isn't present in the gallery at all
        // right now (it was dropped by a previous invalid-combo sync, or never
        // appeared in this fetch's destination list because the server
        // resolved to an unrelated substitute variant), re-materialize it from
        // whatever real <li> markup we've remembered for that exact media id,
        // and insert it into the gallery ourselves. This is what lets a color
        // that was already shown correctly once (e.g. Maroon) keep working
        // every time it's re-selected, even many clicks and several other
        // invalid combos later - the theme's own fetch/sync path can never do
        // this on its own, since the server never reports back an image for an
        // option combination that doesn't exist.
        if (pinnedMediaId && mediaGallerySource && !mediaGallerySource.querySelector(`[data-media-id="${pinnedMediaId}"]`)) {
          const revivedNode = this.getRememberedMediaNode(pinnedMediaId);
          if (revivedNode) {
            mediaGallerySource.prepend(revivedNode);
          }
        }

        if (pinnedMediaId) {
          this.querySelector('media-gallery')?.setActiveMedia?.(pinnedMediaId, true);
        }

        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      /* ---------------------------------------------------------------- */
      /*  Quantity boundaries / rules                                     */
      /* ---------------------------------------------------------------- */

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      /* ---------------------------------------------------------------- */
      /*  Getters                                                         */
      /* ---------------------------------------------------------------- */

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}